/**
 * Logistics Call-Offs Page
 *
 * Restricted view for logistics company users showing only
 * dispatched/delivered call-offs from linked customers.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Loader2, Search, Truck, Package, MapPin, Eye, Warehouse } from 'lucide-react';
import { logisticsAPI, type LogisticsCallOff, type LogisticsCallOffDetail, type LogisticsCallOffLine } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';

export default function LogisticsCallOffs() {
  const { formatDate } = useFormatters();
  const [callOffs, setCallOffs] = useState<LogisticsCallOff[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCallOff, setSelectedCallOff] = useState<LogisticsCallOffDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchCallOffs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (search) params['search'] = search;
      const res = await logisticsAPI.getCallOffs(params);
      setCallOffs(res.data.results ?? (res.data as unknown as LogisticsCallOff[]));
    } catch {
      setCallOffs([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchCallOffs, 300);
    return () => clearTimeout(timer);
  }, [fetchCallOffs]);

  const viewDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await logisticsAPI.getCallOffDetail(id);
      setSelectedCallOff(res.data);
    } catch {
      setSelectedCallOff(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const statusBadge = (status: string) => {
    if (status === 'delivered') {
      return <Badge className="bg-green-100 text-green-800">Delivered</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-800">Dispatched</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Truck className="h-6 w-6" />
          Dispatched Call-Offs
        </h1>
        <p className="text-muted-foreground">
          View dispatched and delivered call-offs from linked customers
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by reference, supplier, or customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Call-offs table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : callOffs.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{search ? 'No call-offs match your search' : 'No dispatched call-offs found'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Dispatched</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {callOffs.map((co) => (
                  <TableRow key={co.id} className="cursor-pointer hover:bg-muted/50" onClick={() => viewDetail(co.id)}>
                    <TableCell className="font-medium">{co.reference}</TableCell>
                    <TableCell>{statusBadge(co.detail_status)}</TableCell>
                    <TableCell>{co.supplier_name}</TableCell>
                    <TableCell>{co.customer_name}</TableCell>
                    <TableCell>{co.pickup_date ? formatDate(co.pickup_date) : '-'}</TableCell>
                    <TableCell>{co.dispatched_at ? formatDate(co.dispatched_at) : '-'}</TableCell>
                    <TableCell className="text-right">{co.total_items}</TableCell>
                    <TableCell className="text-right">{co.total_quantity}</TableCell>
                    <TableCell>{co.destination_name || '-'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); viewDetail(co.id); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {selectedCallOff?.reference ?? 'Call-Off Detail'}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : selectedCallOff ? (
            <div className="space-y-4">
              {/* Header info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  {statusBadge(selectedCallOff.detail_status)}
                </div>
                <div>
                  <span className="text-muted-foreground">Fill %:</span>{' '}
                  {selectedCallOff.fill_percent != null ? `${selectedCallOff.fill_percent}%` : '-'}
                </div>
                <div>
                  <span className="text-muted-foreground">Supplier:</span>{' '}
                  {selectedCallOff.supplier_name}
                </div>
                <div>
                  <span className="text-muted-foreground">Customer:</span>{' '}
                  {selectedCallOff.customer_name}
                </div>
                <div>
                  <span className="text-muted-foreground">Pickup:</span>{' '}
                  {selectedCallOff.pickup_date ? formatDate(selectedCallOff.pickup_date) : '-'}
                </div>
                <div>
                  <span className="text-muted-foreground">Dispatched:</span>{' '}
                  {selectedCallOff.dispatched_at ? formatDate(selectedCallOff.dispatched_at) : '-'}
                </div>
                {selectedCallOff.supplier_address && (
                  <div className="col-span-2 flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Supplier Address:</span>{' '}
                    {selectedCallOff.supplier_address}
                  </div>
                )}
                {selectedCallOff.destination_name && (
                  <div className="col-span-2 flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Destination:</span>{' '}
                    {selectedCallOff.destination_name}
                  </div>
                )}
                {selectedCallOff.notes && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Notes:</span>{' '}
                    {selectedCallOff.notes}
                  </div>
                )}
              </div>

              {/* Lines grouped by delivery type */}
              {selectedCallOff.lines.length > 0 && (
                <DetailLines lines={selectedCallOff.lines} />
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">Failed to load details</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailLines({ lines }: { lines: LogisticsCallOffLine[] }) {
  const warehouseLines = lines.filter(l => l.delivery_type === 'warehouse');
  const directLines = lines.filter(l => l.delivery_type !== 'warehouse');

  return (
    <div>
      <h4 className="text-sm font-medium mb-2">Line Items ({lines.length})</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Part</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Destination</TableHead>
            <TableHead className="text-right">Requested</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {warehouseLines.length > 0 && (
            <>
              <TableRow className="bg-red-50 hover:bg-red-50">
                <TableCell colSpan={4} className="py-2">
                  <div className="flex items-center gap-1.5 font-semibold text-red-700 text-xs uppercase tracking-wide">
                    <Warehouse className="h-3.5 w-3.5" />
                    Warehouse Delivery ({warehouseLines.length} items)
                  </div>
                </TableCell>
              </TableRow>
              {warehouseLines.map(line => (
                <TableRow key={line.id}>
                  <TableCell className="font-medium">{line.part_number}</TableCell>
                  <TableCell>{line.description}</TableCell>
                  <TableCell>{line.delivery_destination_name || 'Warehouse'}</TableCell>
                  <TableCell className="text-right">{line.requested_qty}</TableCell>
                </TableRow>
              ))}
            </>
          )}
          {directLines.length > 0 && (
            <>
              <TableRow className="bg-blue-50 hover:bg-blue-50">
                <TableCell colSpan={4} className="py-2">
                  <div className="flex items-center gap-1.5 font-semibold text-blue-700 text-xs uppercase tracking-wide">
                    <Truck className="h-3.5 w-3.5" />
                    Direct Delivery ({directLines.length} items)
                  </div>
                </TableCell>
              </TableRow>
              {directLines.map(line => (
                <TableRow key={line.id}>
                  <TableCell className="font-medium">{line.part_number}</TableCell>
                  <TableCell>{line.description}</TableCell>
                  <TableCell>{line.delivery_address || 'Direct'}</TableCell>
                  <TableCell className="text-right">{line.requested_qty}</TableCell>
                </TableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
